/**
 * useRasterSourceEffect — Effect 4 of the /map state machine.
 *
 * Spec: docs/superpowers/specs/2026-05-07-map-state-machine.md §7.5
 * Plan: docs/superpowers/plans/2026-05-07-sprint-2026-21-map-refactor.md Task 8
 *
 * Responsibility:
 *   The `ndvi-active-raster` MapLibre source is the single tile source for the
 *   currently-active raster overlay (NDVI / NDVI_CONTRAST / GNR). Its tiles[0]
 *   URL is a pure function of (activeRasterMode, selectedDate, activeScope.id).
 *   When any of those slices change, swap the source so MapLibre kicks off a
 *   fresh tile fetch. When the resulting URL is identical to the last one we
 *   pushed (e.g. a re-render that didn't actually move state), do nothing —
 *   `removeSource + addSource` triggers a style-validation pass and visible
 *   tile flash, both of which we want to avoid.
 *
 * Why this is its own effect, not inline in MapController:
 *   The riskovaja-zone regressions (MapController twice broken / reverted in
 *   sprints 2026-19/20) all stem from imperative source/layer churn scattered
 *   across event handlers. Pulling the URL derivation into a declarative hook
 *   gated by a ref lets the diff-and-swap logic live in exactly one place,
 *   testable in isolation against a mock map.
 *
 * URL shapes (verified against backend/src/ikolhoz/api/v1/ndvi.py):
 *   - 'ndvi'           → /api/v1/ndvi/raster/{date}/ndvi/{z}/{x}/{y}.png
 *   - 'ndvi_contrast'  → /api/v1/ndvi/raster/ndvi-contrast/{date}/{z}/{x}/{y}.png
 *   - 'gnr'            → /api/v1/ndvi/scene-gnr/{date}/{z}/{x}/{y}.png
 *   - 'avg_ndvi'       → null (vector layer, not a raster — handled by other layers)
 *   - 'uniformity'     → null (derived view, not a wire product — see RasterMode §2)
 *   - 'none' or no date → null (overlay off)
 *
 *   `?farm_id={scopeId}` is appended whenever activeScope.id is non-null. The
 *   spec lock-in is "scopeId truthy → farm clip", regardless of scope.type;
 *   the backend ignores the param when it doesn't apply.
 *
 * Source lifecycle:
 *   - URL becomes null → removeSource only (overlay disappears).
 *   - URL becomes non-null and source exists → removeSource + addSource (swap).
 *   - URL becomes non-null and source missing → addSource only (initial mount).
 *   - Each branch updates `lastUrlRef` so subsequent renders compare against
 *     the URL we last actually pushed to MapLibre, not whatever the store
 *     happens to derive.
 *
 * Defensive guards:
 *   - map === null → no-op (initial render before MapLibre boots).
 *   - URL unchanged → no-op (idempotent — protects against StrictMode double
 *     render and reference-equal store updates).
 *
 * Out of scope:
 *   - Adding/removing the *layer* that references this source (registry T6).
 *     If the layer holds a reference to a source we're about to remove,
 *     MapLibre throws — caller must ensure the raster layer is detached or
 *     uses a beforeId pattern. For Sprint 2026-21 the layer is added in
 *     setupMapLayers and never re-references the raster source explicitly,
 *     so removeSource is safe.
 *   - Style validation / TileJSON — we always pass tileSize:256 and a single
 *     tile template; spec §7.5 sample matches.
 */
import { useEffect, useRef, useState } from 'react';
import type maplibregl from 'maplibre-gl';
import { useMapStore } from '../useMapStoreV2';
import type { ScopeType } from '../state/mapState';
import { recreateRasterSource } from '../recreateRasterSource';
import { rasterTileSize } from '../../featureFlags';
import {
  fetchScopeGeomVersion,
  getScopeGeomVersion,
  onScopeGeomVersionRefresh,
  setScopeGeomVersion,
} from '../scopeGeomVersion';

const SOURCE_ID = 'ndvi-active-raster';

// AK-193: keep these in lock-step with the initial source registration in
// v2.tsx (RASTER_MINZOOM/RASTER_MAXZOOM). A scope switch recreates the source,
// so without these the recreated source would silently lose overzoom bounds
// and the flag-driven tileSize that the first-mount registration applied.
const RASTER_MINZOOM = 9;
const RASTER_MAXZOOM = 16;

/**
 * Build the scope query-param suffix for raster URLs.
 *
 * AK-106 (Sprint #2 2026-05-08): satellite products are scope-clipped only
 * for `farm` scope per founder lock-in («Спутниковые продукты работают
 * только при выборе хозяйства»). Task 2 hides satellite controls for
 * non-farm scopes — this helper is the belt-and-suspenders defensive guard:
 * even if a stale `selectedDate` slips through (e.g. user switches scope
 * mid-fetch), URLs without farm_id won't be appended for non-farm scopes.
 *
 * Backend (`/api/v1/ndvi/*`) accepts `farm_id` only — there is no
 * `cluster_id` for raster routes. For non-farm scope.type we emit no
 * query string; backend then has nothing to clip against and returns the
 * full scene. Combined with Task 2 (UI hide), the user never sees this
 * path — но если попадает, mask по hull всё равно работает на бэкенде.
 *
 * Pure function — exported for unit-test stability.
 */
export function buildScopeRasterParam(
  scopeType: ScopeType,
  scopeId: string | null,
  version?: string | null,
): string {
  // AK-587: `_v` = scope field-geometry version. Present in the URI, so a
  // contour edit busts nginx (`$request_uri` is part of proxy_cache_key), the
  // browser cache and MapLibre's tile map in one move — symmetric with the
  // `_v` the MVT source has always carried. When the caller passes nothing we
  // fall back to the registry populated by `useRasterSourceEffect`, so the
  // prewarm/prefetch callers stay in lock-step without signature churn.
  if (!scopeId) return '';
  if (scopeType !== 'farm') return '';
  const v = version === undefined ? getScopeGeomVersion(scopeType, scopeId) : version;
  return v ? `?farm_id=${scopeId}&_v=${encodeURIComponent(v)}` : `?farm_id=${scopeId}`;
}

/**
 * Compute the tile URL for the active raster overlay. Pure function — exported
 * for unit tests so we can pin URL shapes without spinning up a MapLibre stub.
 *
 * Returning null is the explicit "no overlay" signal: caller must remove the
 * source rather than passing null into addSource.
 *
 * AK-106: scope-aware via (scopeType, scopeId). Non-farm scope emits no
 * `farm_id` param (was previously emitted for any non-null scope.id, which
 * misled the backend into clipping by a UUID that wasn't a farm).
 */
export function buildRasterUrl(
  mode: string,
  date: string | null,
  scopeType: ScopeType,
  scopeId: string | null,
  version?: string | null,
): string | null {
  if (mode === 'none' || !date) return null;
  const scopeParam = buildScopeRasterParam(scopeType, scopeId, version);
  switch (mode) {
    case 'ndvi':
      return `/api/v1/ndvi/raster/${date}/ndvi/{z}/{x}/{y}.png${scopeParam}`;
    case 'ndvi_contrast':
      return `/api/v1/ndvi/raster/ndvi-contrast/${date}/{z}/{x}/{y}.png${scopeParam}`;
    case 'gnr':
      return `/api/v1/ndvi/scene-gnr/${date}/{z}/{x}/{y}.png${scopeParam}`;
    default:
      // 'avg_ndvi' / 'uniformity' / unknown → not a raster overlay.
      return null;
  }
}

/**
 * RGB scene URL — Sentinel-2 TCI overlay, scope-aware.
 * Spec §2 / Sprint 2026-19 buildRgbUrl signature.
 *
 * AK-106 (Sprint #2): scope-aware via (scopeType, scopeId).
 */
export function buildRgbUrl(
  on: boolean,
  date: string | null,
  scopeType: ScopeType,
  scopeId: string | null,
  version?: string | null,
): string | null {
  if (!on || !date) return null;
  const scopeParam = buildScopeRasterParam(scopeType, scopeId, version);
  return `/api/v1/ndvi/scene-rgb/${date}/{z}/{x}/{y}.png${scopeParam}`;
}

/**
 * Cloud-mask URL — scope-aware (Founder UAT 2026-05-08 lock-in revert).
 *
 * Previously: scene-wide (AK-71 lock-in «cloud-mask показывает облачность всей
 * сцены»). Founder revert 2026-05-08: «выбрал хозяйство → ничего постороннего
 * не должно ходить». Сейчас cloud-mask следует тому же scope-clip pattern что
 * scene-rgb / scene-gnr / ndvi-raster — `farm_id` query param + backend
 * intersect mask с scope hull.
 *
 * AK-106 (Sprint #2): scope-aware via (scopeType, scopeId).
 */
export function buildCloudMaskUrl(
  on: boolean,
  date: string | null,
  scopeType: ScopeType,
  scopeId: string | null,
  version?: string | null,
): string | null {
  if (!on || !date) return null;
  const scopeParam = buildScopeRasterParam(scopeType, scopeId, version);
  return `/api/v1/ndvi/cloud-mask/${date}/{z}/{x}/{y}.png${scopeParam}`;
}

/**
 * Does swapping `prevUrl` → `url` require a full source recreate, or is a
 * cheap `setTiles()` enough?
 *
 * `setTiles()` keeps MapLibre's in-memory tile map (keyed by sourceId + z/x/y,
 * NOT by URL), so already-painted pixels survive the swap. That is fine for a
 * date / product toggle — those repaint anyway — but wrong whenever the pixels
 * for the *same* (z,x,y) now mean something else:
 *
 *   - `farm_id` changed  → AK-119: tiles of the previous farm kept bleeding
 *     through after a scope switch (founder symptom: Шпаковский fields still
 *     visible after selecting «Руно»).
 *   - `_v` changed       → AK-587: the scope's field geometry moved, so the
 *     clip under the raster differs. Same class of staleness.
 *
 * Pure + exported so the rule can be pinned in tests without a MapLibre stub.
 */
export function rasterUrlNeedsRecreate(prevUrl: string | null, url: string | null): boolean {
  return (
    extractFarmId(prevUrl) !== extractFarmId(url) ||
    extractGeomVersion(prevUrl) !== extractGeomVersion(url)
  );
}

const RGB_SOURCE_ID = 'rgb-scene-raster';
const CLOUD_SOURCE_ID = 'cloud-mask-raster';

/**
 * Extract the `farm_id` query-param value from a tile URL, or `null` when
 * the param is absent. Tolerates the `{z}/{x}/{y}` placeholders that are
 * present in tile-template URLs (URL constructor would reject them, so we
 * parse manually).
 *
 * AK-119: this is the discriminator that decides whether a URL change is a
 * scope shift (must fully evict cache) or a date/product toggle within the
 * same scope (safe to `setTiles`).
 */
function extractFarmId(url: string | null): string | null {
  if (!url) return null;
  const m = url.match(/[?&]farm_id=([^&]+)/);
  return m ? decodeURIComponent(m[1]!) : null;
}

/**
 * Extract the `_v` (scope geometry version) param — AK-587.
 *
 * A version change means the field geometry under the raster moved, so the
 * already-painted tiles are wrong in exactly the same way stale cross-scope
 * tiles are. `setTiles()` alone would leave them on screen (MapLibre keys its
 * in-memory cache by (sourceId, z, x, y), not by URL), so we route a version
 * change through the same full-recreate path as a scope shift.
 */
function extractGeomVersion(url: string | null): string | null {
  if (!url) return null;
  const m = url.match(/[?&]_v=([^&]+)/);
  return m ? decodeURIComponent(m[1]!) : null;
}

/**
 * setSourceTiles — swap a raster source URL.
 *
 * AK-119 hotfix: when the new URL targets a DIFFERENT scope (farm_id query
 * param appeared / disappeared / changed value) we cannot use `setTiles()`
 * alone — MapLibre's (sourceId, z, x, y) tile cache survives the URL swap
 * and continues to paint stale coloured tiles over areas outside the new
 * scope (founder symptom: NDVI tiles for Шпаковский/Кочубеевский still
 * visible after selecting farm «Руно»). On scope-shift we route through
 * `recreateRasterSource` (full removeSource + addSource cycle, evicts cache).
 *
 * On non-scope changes (date / product toggle within the same farm) we keep
 * the cheap `setTiles()` path to avoid the style-validation flash that the
 * UAT spec § 7.5 explicitly accepts.
 *
 * `prevUrl` is the URL the caller last pushed; pass `null` for first-mount.
 */
function setSourceTiles(
  map: maplibregl.Map,
  sourceId: string,
  url: string | null,
  prevUrl: string | null,
): void {
  const existing = map.getSource(sourceId) as
    | (maplibregl.RasterTileSource & { setTiles?: (u: string[]) => void })
    | undefined;

  if (url === null) {
    // Source-on-empty pattern: keep source registered с EMPTY_TILE so layer
    // referencing it doesn't crash on visibility=visible toggles.
    const EMPTY_TILE = 'data:,';
    if (existing?.setTiles) existing.setTiles([EMPTY_TILE]);
    return;
  }

  if (existing && rasterUrlNeedsRecreate(prevUrl, url)) {
    // Full source recreation evicts the tile cache so stale colored pixels
    // from the previous scope cannot leak into the new scope's view.
    // AK-193: preserve overzoom bounds + flag-driven tileSize across recreate.
    recreateRasterSource(map, sourceId, url, {
      type: 'raster',
      tileSize: rasterTileSize(),
      minzoom: RASTER_MINZOOM,
      maxzoom: RASTER_MAXZOOM,
    });
    return;
  }

  if (existing?.setTiles) {
    existing.setTiles([url]);
    return;
  }
  // Fallback: source not yet registered (shouldn't happen в normal lifecycle —
  // initial style pre-registers all 3 raster sources w/ EMPTY_TILE).
  if (map.getSource(sourceId)) map.removeSource(sourceId);
  map.addSource(sourceId, {
    type: 'raster',
    tiles: [url],
    tileSize: rasterTileSize(),
    minzoom: RASTER_MINZOOM,
    maxzoom: RASTER_MAXZOOM,
  });
}

export function useRasterSourceEffect(map: maplibregl.Map | null): void {
  // Per-field selectors — each subscription only fires on its own slice change,
  // so a basemap toggle doesn't re-run this effect.
  const activeRasterMode = useMapStore((s) => s.activeRasterMode);
  const selectedDate = useMapStore((s) => s.selectedDate);
  const rgbSceneOn = useMapStore((s) => s.rgbSceneOn);
  const cloudMaskOn = useMapStore((s) => s.cloudMaskOn);
  // AK-106: scope.type is now significant — non-farm scope returns empty
  // scope-param (see buildScopeRasterParam). Subscribe to both fields.
  const activeScopeType = useMapStore((s) => s.activeScope.type);
  const activeScopeId = useMapStore((s) => s.activeScope.id);

  // AK-587: scope field-geometry version — the raster cache-buster. Kept in
  // local state (not the shared store) so this stays inside the satellite
  // vertical; published to the module registry so prewarm/prefetch builders,
  // which have no access to this hook, produce byte-identical URLs.
  const [geomVersion, setGeomVersion] = useState<string | null>(() =>
    getScopeGeomVersion(activeScopeType, activeScopeId),
  );

  // Bumped by `requestScopeGeomVersionRefresh()` — fired where the vector layer
  // invalidates itself after a field save (`invalidateFieldsMvt`, AK-334). Without
  // it the version is only re-read on a scope switch, so «сохранил контур и остался
  // на той же ферме» would keep the pre-edit PNG alive in nginx for 30 days.
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => onScopeGeomVersionRefresh(() => setRefreshTick((n) => n + 1)), []);

  useEffect(() => {
    if (activeScopeType !== 'farm' || !activeScopeId) {
      setGeomVersion(null);
      return;
    }
    const known = getScopeGeomVersion(activeScopeType, activeScopeId);
    // Show the known token immediately; still refetch, because the whole point
    // is noticing that the geometry changed since we last looked.
    setGeomVersion(known);
    const ac = new AbortController();
    fetchScopeGeomVersion(activeScopeType, activeScopeId, ac.signal)
      .then((v) => {
        if (ac.signal.aborted) return;
        setScopeGeomVersion(activeScopeType, activeScopeId, v);
        setGeomVersion(v);
      })
      .catch(() => {
        // Best-effort: without `_v` we fall back to pre-AK-587 URLs. The
        // backend still folds the version into its own cache key, so the
        // rendered pixels stay correct — only the edge cache lags.
      });
    return () => ac.abort();
  }, [activeScopeType, activeScopeId, refreshTick]);

  // Tracks the URLs we last pushed to MapLibre.
  const lastNdviUrlRef = useRef<string | null>(null);
  const lastRgbUrlRef = useRef<string | null>(null);
  const lastCloudUrlRef = useRef<string | null>(null);

  // NDVI active overlay — uses setTiles since source is pre-registered in
  // initial style with EMPTY_TILE (post-merge hotfix). This keeps layer
  // references stable + avoids style validation flash на каждом swap.
  useEffect(() => {
    if (!map) return;
    const url = buildRasterUrl(activeRasterMode, selectedDate, activeScopeType, activeScopeId, geomVersion);
    if (url === lastNdviUrlRef.current) return;
    setSourceTiles(map, SOURCE_ID, url, lastNdviUrlRef.current);
    lastNdviUrlRef.current = url;
  }, [map, activeRasterMode, selectedDate, activeScopeType, activeScopeId, geomVersion]);

  // RGB scene overlay (T13.5: regression fix — was missing post-T13)
  useEffect(() => {
    if (!map) return;
    const url = buildRgbUrl(rgbSceneOn, selectedDate, activeScopeType, activeScopeId, geomVersion);
    if (url === lastRgbUrlRef.current) return;
    setSourceTiles(map, RGB_SOURCE_ID, url, lastRgbUrlRef.current);
    lastRgbUrlRef.current = url;
  }, [map, rgbSceneOn, selectedDate, activeScopeType, activeScopeId, geomVersion]);

  // Cloud-mask overlay (T13.5 regression fix + 2026-05-08 scope-aware).
  // Founder UAT 2026-05-08 lock-in: cloud-mask теперь scope-clipped — same
  // signature как rgb/gnr (передаём activeScopeId).
  useEffect(() => {
    if (!map) return;
    const url = buildCloudMaskUrl(cloudMaskOn, selectedDate, activeScopeType, activeScopeId, geomVersion);
    if (url === lastCloudUrlRef.current) return;
    setSourceTiles(map, CLOUD_SOURCE_ID, url, lastCloudUrlRef.current);
    lastCloudUrlRef.current = url;
  }, [map, cloudMaskOn, selectedDate, activeScopeType, activeScopeId, geomVersion]);
}
